import {
  type MagnusUIMessage,
  type SdkApprovalRequest,
  type SdkApprovalResponse,
  sdkApprovalRequestSchema,
} from "@bg-bgi/magnus-sdk";
import type { ApprovalChoice, ApprovalCopy } from "./approval-input";

/**
 * Host-supplied I/O for resolving HITL approval pauses (ADR-050). The block
 * watches the transcript for `data-approval-request` parts, renders the
 * approval card in place of the composer, and hands the decision to
 * `submit`. `useMagnusConversation` binds this over
 * `client.toolApprovals.submit` (`POST /api/sdk/v1/tool-approvals`).
 */
export interface ChatApprovalsSource {
  /** Surface-specific overrides for the card's copy. */
  copy?: ApprovalCopy;
  /** Invoked after a successful `always` / `approve-all` submission. */
  onPersisted?(): void | Promise<void>;
  /**
   * POST the decision to the resolution route. `editedArgs` carries values
   * the user changed on the card — only on allow-decisions with actual
   * edits; the workflow sanitizes them against the tool's declared
   * editable fields.
   */
  submit(input: SdkApprovalResponse): Promise<unknown>;
}

/**
 * A paused, destructive tool call awaiting the user's decision. The block
 * renders it as the approval card (replacing the composer) until resolved.
 * Unlike multiple choice, the decision resolves the workflow through
 * {@link ChatApprovalsSource.submit} rather than by sending a message.
 */
export type ActiveApproval = {
  copy?: ApprovalCopy;
  onResolve: (
    choice: ApprovalChoice,
    editedArgs?: Record<string, unknown>
  ) => void;
  request: SdkApprovalRequest;
  toolCallId: string;
};

/**
 * Returns the first unresolved `data-approval-request` across assistant
 * messages, or `null` if there is none. Resolved tool-call ids are skipped
 * so a decision (which resolves out-of-band, not by appending a message)
 * dismisses the card without re-surfacing it. A payload that fails
 * `sdkApprovalRequestSchema` is skipped rather than rendered half-formed.
 */
export function findPendingApproval(
  messages: readonly MagnusUIMessage[] | undefined,
  resolved: ReadonlySet<string>
): SdkApprovalRequest | null {
  if (!messages) {
    return null;
  }
  for (const msg of messages) {
    if (msg.role !== "assistant") {
      continue;
    }
    for (const part of msg.parts ?? []) {
      if (
        typeof part !== "object" ||
        part === null ||
        (part as { type?: string }).type !== "data-approval-request"
      ) {
        continue;
      }
      const parsed = sdkApprovalRequestSchema.safeParse(
        (part as { data?: unknown }).data
      );
      if (!parsed.success || resolved.has(parsed.data.toolCallId)) {
        continue;
      }
      return parsed.data;
    }
  }
  return null;
}
